import posthog from 'posthog-js'
import { consentFromCookieString } from '@lib/consent/consent'
import { applyConsentToPostHog } from '@lib/consent/client'
import { isPostHogCaptureEnabled } from '@lib/posthog-enabled'
import { registerExceptionTransport } from '@lib/posthog-logging-service'
import { applyLcpElementMeta, latestLcpCapture, type LcpCapture } from '@lib/web-vitals-lcp'
import { normalizeExceptionEvent } from '@lib/posthog-exception-properties'
import { dropInjectedBridgeExceptions } from '@lib/posthog-injected-bridge-exceptions'
import { browserMetricsConfig } from '@lib/posthog-metrics'

// Localhost never initializes the SDK — no events, decide, flags, or
// recorder traffic. Playwright e2e re-enables capture (see isPostHogCaptureEnabled).
if (isPostHogCaptureEnabled()) {
  // Captured before posthog.init() so the observer is listening when the
  // browser finalizes the LCP entry; injected into $web_vitals via before_send
  // below. Recorded with the page it was observed on, because this module
  // outlives soft navigations and the element does not carry over.
  let lcpCapture: LcpCapture | null = null

  if (typeof PerformanceObserver !== 'undefined') {
    new PerformanceObserver((list) => {
      lcpCapture = latestLcpCapture(list.getEntries(), window.location.href)
    }).observe({ type: 'largest-contentful-paint', buffered: true })
  }

  // Initialize opted-out and cookieless: nothing is captured and no ph_*
  // cookie/localStorage is written until the visitor grants analytics consent
  // (GDPR/ePrivacy). The ConsentBanner opts in at decision time; here we only
  // restore a previously granted choice across page loads.
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN!, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    defaults: '2025-05-24',
    capture_exceptions: true,
    // Exception autocapture emits only `$exception_list`, while
    // `captureException` also sets `$exception_type` / `$exception_message`.
    // The dashboard filters on the flat pair, so without this every unhandled
    // crash arrives with both as null. See lib/posthog-exception-properties.ts.
    // Also injects LCP element metadata captured by the PerformanceObserver
    // above into $web_vitals events.
    //
    // Dropping runs first: in-app browsers (Instagram/Facebook) inject a
    // telemetry script whose native-bridge teardown throws into our
    // window.onerror, filing crashes against us that contain no frame of ours.
    // See lib/posthog-injected-bridge-exceptions.ts. Nothing downstream needs
    // to normalise an event we are not sending.
    before_send: (event) => {
      const kept = dropInjectedBridgeExceptions(event)
      const normalized = normalizeExceptionEvent(kept)
      return normalized ? applyLcpElementMeta(normalized, lcpCapture) : normalized
    },
    debug: process.env.NODE_ENV === 'development',
    opt_out_capturing_by_default: true,
    capture_dead_clicks: true,
    persistence: 'memory',
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    // posthog-js silently drops all capture() calls when navigator.webdriver
    // is true (its built-in bot filter) — which is always true under
    // Playwright. Only disable that filter for e2e runs; production keeps
    // filtering real bot traffic.
    opt_out_useragent_filter: process.env.NEXT_PUBLIC_PLAYWRIGHT_E2E === 'true',
    // Application metrics (incl. fetch/XHR timing). The SDK discards samples
    // while not capturing, so the consent gate above covers them too.
    metrics: browserMetricsConfig(),
  })

  // Points the PostHog logging service at posthog-js, so a `logger.error` in a
  // client component produces the same `$exception` shape as one on the server.
  // posthog-js drops captures while opted out, so this inherits the consent gate
  // above rather than needing its own; the distinct id is likewise the SDK's,
  // not the placeholder the service passes for anonymous server logs.
  registerExceptionTransport({
    captureException(error, properties) {
      posthog.captureException(error, properties)
    },
  })

  const consent = consentFromCookieString(document.cookie)
  const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  if (consent?.analytics === true && !gpc) {
    applyConsentToPostHog(consent)
  }
}
